import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import api, { formatApiError } from "@/lib/api";
import { KeyRound, ShieldCheck, AlertTriangle, ArrowLeft } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();

  const [tokenState, setTokenState] = useState("checking"); // checking | valid | invalid
  const [tokenError, setTokenError] = useState("");
  const [email, setEmail] = useState("");
  const [purpose, setPurpose] = useState("reset"); // reset | onboarding
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");

  useEffect(() => {
    let cancelled = false;
    async function check() {
      if (!token) {
        setTokenState("invalid");
        setTokenError("Aucun jeton fourni. Vérifiez le lien reçu par e-mail.");
        return;
      }
      try {
        const { data } = await api.get(`/auth/reset-token/${encodeURIComponent(token)}`);
        if (!cancelled) {
          setEmail(data.email || "");
          setPurpose(data.purpose || "reset");
          setTokenState("valid");
        }
      } catch (err) {
        if (!cancelled) {
          setTokenError(formatApiError(err));
          setTokenState("invalid");
        }
      }
    }
    check();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setFormError("");
    if (password.length < 8) {
      setFormError("Le mot de passe doit contenir au moins 8 caractères.");
      return;
    }
    if (password !== confirm) {
      setFormError("Les deux mots de passe ne correspondent pas.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success(
        purpose === "onboarding"
          ? "Votre compte est activé. Vous pouvez maintenant vous connecter."
          : "Mot de passe réinitialisé. Vous pouvez maintenant vous connecter."
      );
      nav("/connexion", { replace: true });
    } catch (err) {
      setFormError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const isOnboarding = purpose === "onboarding";
  const headerEyebrow = isOnboarding ? "Activation de compte" : "Réinitialisation";
  const headerTitle = isOnboarding ? "Bienvenue — définissez votre mot de passe" : "Définir un nouveau mot de passe";
  const heroEyebrow = isOnboarding ? "Premier accès" : "Nouveau mot de passe";
  const heroTitle = isOnboarding
    ? "Activez votre compte UNISIC-Recherche"
    : "Choisissez un mot de passe robuste";
  const submitLabel = isOnboarding ? "Activer mon compte" : "Enregistrer le nouveau mot de passe";

  return (
    <div className="min-h-[calc(100vh-4rem)] grid lg:grid-cols-2" data-testid="reset-password-page">
      <div className="hidden lg:block relative">
        <img
          src="/media/research-lab.jpg"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-unisic-blue/80" />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <Logo variant="light" />
          <div>
            <div className="eyebrow" style={{ color: "#E5C158" }}>{heroEyebrow}</div>
            <h2 className="mt-3 text-3xl font-bold leading-tight">
              {heroTitle}
            </h2>
            <p className="mt-4 text-white/80 text-sm max-w-md leading-relaxed">
              Utilisez au moins 8 caractères, avec un mélange de majuscules, minuscules,
              chiffres et symboles pour renforcer la sécurité de votre compte.
            </p>
            <div className="mt-8 flex items-center gap-2 text-xs uppercase tracking-widest text-unisic-gold">
              <ShieldCheck className="w-4 h-4" /> Chiffrement bcrypt
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-md">
          <div className="eyebrow eyebrow-dark">{headerEyebrow}</div>
          <h1 className="mt-2 text-3xl font-bold text-unisic-blue">
            {headerTitle}
          </h1>

          {tokenState === "checking" && (
            <p className="mt-6 text-sm text-slate-500" data-testid="token-checking">
              Vérification du lien en cours…
            </p>
          )}

          {tokenState === "invalid" && (
            <div
              className="mt-8 paper-card p-6 border-l-4 border-unisic-red bg-red-50/50"
              data-testid="token-invalid"
            >
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-unisic-red flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold text-unisic-blue">Lien invalide ou expiré</div>
                  <p className="mt-2 text-sm text-slate-700 leading-relaxed">
                    {tokenError || "Ce lien de réinitialisation n'est plus valide."}
                  </p>
                  <Link
                    to="/mot-de-passe-oublie"
                    className="mt-4 inline-block link-underline text-sm"
                    data-testid="link-request-new"
                  >
                    Demander un nouveau lien
                  </Link>
                </div>
              </div>
            </div>
          )}

          {tokenState === "valid" && (
            <>
              <p className="mt-2 text-sm text-slate-600">
                Compte&nbsp;: <span className="font-semibold text-unisic-blue">{email}</span>
              </p>
              <form onSubmit={submit} className="mt-8 space-y-4" data-testid="reset-form">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-slate-600">
                    Nouveau mot de passe
                  </label>
                  <input
                    type="password"
                    required
                    minLength={8}
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="mt-1 w-full border border-slate-300 px-4 py-3 rounded-sm focus:outline-none focus:ring-2 focus:ring-unisic-gold text-sm"
                    data-testid="reset-input-password"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-slate-600">
                    Confirmer le mot de passe
                  </label>
                  <input
                    type="password"
                    required
                    minLength={8}
                    autoComplete="new-password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    className="mt-1 w-full border border-slate-300 px-4 py-3 rounded-sm focus:outline-none focus:ring-2 focus:ring-unisic-gold text-sm"
                    data-testid="reset-input-confirm"
                  />
                </div>

                {formError && (
                  <div
                    className="text-sm text-unisic-red bg-red-50 border border-red-100 px-3 py-2 rounded-sm"
                    data-testid="reset-error"
                  >
                    {formError}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-primary w-full justify-center"
                  data-testid="reset-submit"
                >
                  <KeyRound className="w-4 h-4" />
                  {submitting ? "Enregistrement..." : submitLabel}
                </button>
              </form>
            </>
          )}

          <div className="mt-10 pt-6 border-t border-slate-200 text-xs text-slate-500 flex items-center gap-2">
            <ArrowLeft className="w-3.5 h-3.5" />
            <Link to="/connexion" className="hover:text-unisic-blue">
              Retour à la connexion
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
